import { OpenAPI } from "../client"
import { request as __request } from "../client/core/request"

export type ProductionMaterialAvailabilitySelection = {
  technology_input_id: string
  product_id: string
  scrap_qty?: number
}

export type ProductionMaterialAvailabilityCheck = {
  order_id: string
  run_count?: number
  selections?: ProductionMaterialAvailabilitySelection[]
}

export type ProductionMaterialAvailabilityRequest = {
  checks: ProductionMaterialAvailabilityCheck[]
}

export type ProductionMaterialAvailabilityProduct = {
  id: string
  sku: string
  name: string
  unit: string
}

export type ProductionMaterialAvailabilityAlternative = {
  product: ProductionMaterialAvailabilityProduct
  required_qty: number
  available_qty: number
  enough: boolean
}

export type ProductionMaterialAvailabilityComponent = {
  technology_input_id: string
  technology_input_ids?: string[]
  product: ProductionMaterialAvailabilityProduct
  required_qty: number
  available_qty: number
  missing_qty: number
  is_service: boolean
  status: "ready" | "shortage"
  alternatives?: ProductionMaterialAvailabilityAlternative[]
}

export type ProductionMaterialAvailabilityResult = {
  order_id: string
  run_count: number
  status: "ready" | "shortage" | "not_executable"
  reason?: string
  scope: "warehouse_aggregate"
  components: ProductionMaterialAvailabilityComponent[]
}

export type ProductionMaterialAvailabilityResponse = {
  checks: ProductionMaterialAvailabilityResult[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value)

const round3 = (value: number) => Math.round(value * 1000) / 1000

const isIdentifier = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  )

const assertProduct: (
  value: unknown,
) => asserts value is ProductionMaterialAvailabilityProduct = (value) => {
  if (
    !isRecord(value) ||
    !isIdentifier(value.id) ||
    typeof value.sku !== "string" ||
    typeof value.name !== "string" ||
    typeof value.unit !== "string"
  ) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
}

const assertComponent: (
  value: unknown,
) => asserts value is ProductionMaterialAvailabilityComponent = (value) => {
  if (!isRecord(value)) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
  if (
    !isIdentifier(value.technology_input_id) ||
    !isRecord(value.product) ||
    !isFiniteNumber(value.required_qty) ||
    value.required_qty < 0 ||
    !isFiniteNumber(value.available_qty) ||
    value.available_qty < 0 ||
    !isFiniteNumber(value.missing_qty) ||
    value.missing_qty < 0 ||
    typeof value.is_service !== "boolean" ||
    (value.status !== "ready" && value.status !== "shortage")
  ) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
  assertProduct(value.product)
  if (value.technology_input_ids !== undefined) {
    if (
      !Array.isArray(value.technology_input_ids) ||
      value.technology_input_ids.length === 0 ||
      value.technology_input_ids.some((id) => !isIdentifier(id)) ||
      new Set(value.technology_input_ids).size !==
        value.technology_input_ids.length ||
      !value.technology_input_ids.includes(value.technology_input_id)
    ) {
      throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
    }
  }
  if (value.alternatives !== undefined) {
    if (!Array.isArray(value.alternatives)) {
      throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
    }
    for (const alternative of value.alternatives) {
      if (
        !isRecord(alternative) ||
        !isFiniteNumber(alternative.required_qty) ||
        alternative.required_qty < 0 ||
        !isFiniteNumber(alternative.available_qty) ||
        alternative.available_qty < 0 ||
        typeof alternative.enough !== "boolean"
      ) {
        throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
      }
      assertProduct(alternative.product)
    }
  }
}

const assertResultSemantics = (value: ProductionMaterialAvailabilityResult) => {
  for (const component of value.components) {
    const expectedMissing = Math.max(
      round3(component.required_qty - component.available_qty),
      0,
    )
    if (Math.abs(component.missing_qty - expectedMissing) > 0.000001) {
      throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
    }
    const isShortage = expectedMissing > 0
    if (
      (component.status === "shortage" && !isShortage) ||
      (component.status === "ready" && isShortage)
    ) {
      throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
    }
  }
  if (
    value.status === "ready" &&
    value.components.some((component) => component.status === "shortage")
  ) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
  if (
    value.status === "shortage" &&
    !value.components.some(
      (component) =>
        component.status === "shortage" && component.missing_qty > 0,
    )
  ) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
  if (value.status === "not_executable" && value.components.length > 0) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
}

export const normalizeProductionMaterialAvailabilityResponse = (
  raw: unknown,
  expectedOrderIds: string[],
): ProductionMaterialAvailabilityResponse => {
  if (!isRecord(raw) || !Array.isArray(raw.checks)) {
    throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
  }
  const expected = new Set(expectedOrderIds)
  if (raw.checks.length !== expected.size) {
    throw new Error("Niepełna odpowiedź dostępności materiałów.")
  }
  const seen = new Set<string>()
  const checks: ProductionMaterialAvailabilityResult[] = []
  for (const value of raw.checks) {
    if (
      !isRecord(value) ||
      !isIdentifier(value.order_id) ||
      !expected.has(value.order_id) ||
      seen.has(value.order_id) ||
      !isFiniteNumber(value.run_count) ||
      value.run_count < 0 ||
      (value.status !== "ready" &&
        value.status !== "shortage" &&
        value.status !== "not_executable") ||
      value.scope !== "warehouse_aggregate" ||
      !Array.isArray(value.components)
    ) {
      throw new Error("Nieprawidłowa odpowiedź dostępności materiałów.")
    }
    for (const component of value.components) assertComponent(component)
    assertResultSemantics(
      value as unknown as ProductionMaterialAvailabilityResult,
    )
    seen.add(value.order_id)
    checks.push(value as unknown as ProductionMaterialAvailabilityResult)
  }
  if (seen.size !== expected.size) {
    throw new Error("Niepełna odpowiedź dostępności materiałów.")
  }
  return { checks }
}

export const checkProductionMaterialAvailability = (
  body: ProductionMaterialAvailabilityRequest,
): Promise<ProductionMaterialAvailabilityResponse> =>
  __request<unknown>(OpenAPI, {
    method: "POST",
    url: "/v1/production-orders/material-availability",
    body,
    mediaType: "application/json",
  }).then((response) =>
    normalizeProductionMaterialAvailabilityResponse(
      response,
      body.checks.map((check) => check.order_id),
    ),
  )
